import type { SRT_Column, SRT_Row, SRT_RowData } from '../types'
import type { Viewport } from './types'

import { getViewport } from './helpers'
import { memo } from './memo'
import { getCollapsedGrids } from './utils'

export type Rect = {
	width: number
	height: number
}

export type ElementDimm = {
	scrollTop: number
	scrollLeft: number
	width: number
	height: number
}

export type VirtOptions<T extends SRT_RowData> = {
	enabled?: boolean
	getScrollElement: () => HTMLElement
	columns: SRT_Column<T, unknown>[]
	rows: SRT_Row<T>[]
	getRowHeight: (row?: SRT_Row<T>) => number
	getColSize:
		| ((column: SRT_Column<T>) => number)
		| ((column?: SRT_Column<T>) => undefined)
	onChange?(instance: Virt<T>, sync: boolean): void
}
export class Virt<T extends SRT_RowData> {
	isScrolling: boolean = false
	targetWindow: (Window & typeof globalThis) | null = null
	scrollElement: HTMLElement | null = null
	scrollRect: Rect = { width: 0, height: 0 }
	dimm: ElementDimm = {
		scrollTop: 0,
		scrollLeft: 0,
		width: 0,
		height: 0,
	}
	viewport: Viewport = {
		columns: [[0, 0]],
		rows: [0, 0],
	}
	options: VirtOptions<T> = {
		getScrollElement: () => document.body,
		columns: [],
		rows: [],
		getRowHeight: () => 0,
		getColSize: (column: SRT_Column<T>) => column?.getSize(),
	}

	constructor(options: VirtOptions<T>) {
		this.options = {
			...this.options,
			...options,
		}
	}
	private notify = (sync: boolean) => {
		this.options.onChange?.(this, sync)
	}
	private maybeNotify = memo(
		() => [this.isScrolling, this.getViewport()],
		(isScrolling) => {
			this.notify(isScrolling)
		},
		{
			key: process.env.NODE_ENV !== 'production' && 'maybeNotify',
		}
	)
	private cleanup = () => {
		this.scrollElement = null
		this.targetWindow = null
	}
	setOptions = (opts: VirtOptions<T>) => {
		Object.entries(opts).forEach(([key, value]) => {
			if (typeof value === 'undefined') delete (opts as any)[key]
		})
		this.options = {
			...this.options,
			...opts,
		}
	}
	getDimmensions = () => this.dimm
	getViewport = memo(
		() => [this.options.rows, this.options.columns, this.getDimmensions()],
		(bodyRows, columns, dimm) => {
			const newWieport = getViewport<T>(
				{
					top: dimm.scrollTop,
					left: dimm.scrollLeft,
					height: dimm.height,
					width: dimm.width,
					skipItems: [0, 0],
				},
				{
					columns,
					bodyRows,
					getRowSize: this.options.getRowHeight,
					getColSize: this.options.getColSize,
				}
			)
			this.viewport = newWieport

			return this.viewport
		},
		{ key: 'getViewport' }
	)

	didMount = () => () => {
		this.cleanup()
	}

	willUpdate = () => {
		const scrollElement = this.options.enabled
			? this.options.getScrollElement()
			: null

		if (this.scrollElement !== scrollElement) {
			this.cleanup()

			if (!scrollElement) {
				this.maybeNotify()

				return
			}

			this.scrollElement = scrollElement

			if (this.scrollElement && 'ownerDocument' in this.scrollElement) {
				this.targetWindow = this.scrollElement.ownerDocument.defaultView
			} else {
				this.targetWindow = (this.scrollElement as any)?.window ?? null
			}
			observeElementRect(this, (rect) => {
				this.dimm = { ...this.dimm, ...rect }
				this.maybeNotify()
			})
			observeElementOffset(this, (dimm, isScrolling) => {
				this.dimm = { ...dimm }
				this.isScrolling = isScrolling
				this.maybeNotify()
			})
		}
	}
	getGrid = memo(
		() => [
			this.getViewport(),
			this.options.rows,
			this.options.columns,
			this.getDimmensions(),
			this.options.getRowHeight,
		],
		(viewport, bodyRows, columns, dimm, getRowHeight) => {
			const headerRows: SRT_Row<T>[] = []
			const footerRows: SRT_Row<T>[] = []
			const getColumnWidth = (column: SRT_Column<T>) => column?.getSize()
			const getCellColSpan = () => 1

			const skipItems: [number, number] = [0, 0]

			return getCollapsedGrids<T>({
				headerRows,
				bodyRows,
				footerRows,
				columns,
				skipItems,
				loadedRowsStart: 0,
				totalRowCount: bodyRows.length,
				viewport,
				getCellColSpan,
				getRowHeight,
				getColumnWidth,
			})
		},
		{
			key: 'getGrid',
		}
	)
}

export const observeElementOffset = <T extends SRT_RowData>(
	instance: Virt<T>,
	cb: (dimm: ElementDimm, isScrolling: boolean) => void
) => {
	const element = instance.scrollElement
	if (!element) {
		return
	}

	const createHandler = (isScrolling: boolean) => () => {
		cb(
			{
				scrollTop: element.scrollTop,
				scrollLeft: element.scrollLeft,
				width: element.clientWidth,
				height: element.clientHeight,
			},
			isScrolling
		)
	}
	const handler = createHandler(true)
	const endHandler = createHandler(false)
	endHandler()

	element.addEventListener('scroll', handler, { passive: true })
	element.addEventListener('scrollend', endHandler, { passive: true })

	return () => {
		element.removeEventListener('scroll', handler)
		element.removeEventListener('scrollend', endHandler)
	}
}

export const observeElementRect = <T extends SRT_RowData>(
	instance: Virt<T>,
	cb: (rect: Rect) => void
) => {
	const element = instance.scrollElement
	if (!element) {
		return
	}
	const targetWindow = instance.targetWindow
	if (!targetWindow) {
		return
	}

	const handler = (rect: Rect) => {
		const { width, height } = rect
		cb({ width: Math.round(width), height: Math.round(height) })
	}

	handler(element.getBoundingClientRect())

	if (!targetWindow.ResizeObserver) {
		return () => {}
	}

	const observer = new targetWindow.ResizeObserver((entries) => {
		const entry = entries[0]

		if (entry?.borderBoxSize) {
			const box = entry.borderBoxSize[0]
			if (box) {
				handler({ width: box.inlineSize, height: box.blockSize })

				return
			}
		}
		handler(element.getBoundingClientRect())
	})

	observer.observe(element, { box: 'border-box' })

	return () => {
		observer.unobserve(element)
	}
}
